import * as React from 'react';
import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SmallModal from '../global/SmallModal';
import { formatDollarAmount } from '../../utils/global/helper';

type Props = {
  open: boolean;
  onClose: () => void;
  onDismissSuccess: () => void;
  saleTotalDollars: number;
  isSplitPayment?: boolean;
};

function CashTenderModal({
  open,
  onClose,
  onDismissSuccess,
  saleTotalDollars,
  isSplitPayment = false,
}: Props) {
  const [cashReceived, setCashReceived] = React.useState('');
  const [changeDue, setChangeDue] = React.useState(0);
  const [isSuccess, setIsSuccess] = React.useState(false);

  const cashReceivedNum = parseFloat(cashReceived) || 0;
  const isTenderEnabled =
    cashReceived !== '' && cashReceivedNum >= saleTotalDollars;

  React.useEffect(() => {
    if (open) {
      setCashReceived('');
      setChangeDue(0);
      setIsSuccess(false);
    }
  }, [open]);

  const handleTender = () => {
    setChangeDue(cashReceivedNum - saleTotalDollars);
    setIsSuccess(true);
  };

  const saleTotalFormatted = formatDollarAmount(saleTotalDollars);
  const Icon = isSplitPayment ? AttachMoneyIcon : CheckCircleIcon;

  if (isSuccess) {
    return (
      <SmallModal
        open={open}
        onClose={onDismissSuccess}
        hideCloseButton
        footer={{
          button: {
            text: 'Done',
            type: 'button',
            onClick: onDismissSuccess,
          },
        }}>
        <Stack
          p={isSplitPayment ? 2 : 4}
          gap={2}
          alignItems="center"
          justifyContent="center">
          <Icon sx={{ fontSize: 48, color: 'success.main' }} />
          {!isSplitPayment && (
            <Typography variant="h5" textAlign="center">
              Payment Recorded
            </Typography>
          )}
          <Typography
            variant={isSplitPayment ? 'h5' : 'body2'}
            color={isSplitPayment ? undefined : 'text.secondary'}
            textAlign="center">
            Change due to customer
          </Typography>
          <Typography variant="h5" textAlign="center">
            {formatDollarAmount(Math.max(0, changeDue))}
          </Typography>
        </Stack>
      </SmallModal>
    );
  }

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      title={`Sale Total: ${saleTotalFormatted}`}
      footer={{
        button: {
          text: 'Tender',
          type: 'button',
          disabled: !isTenderEnabled,
          onClick: handleTender,
        },
      }}>
      <Stack
        p={3}
        gap={3}
        component="form"
        onSubmit={handleTender}
        boxSizing="border-box"
        width="100%">
        <TextField
          label="Cash Received"
          value={cashReceived}
          onChange={e => setCashReceived(e.target.value)}
          type="number"
          slotProps={{
            htmlInput: { min: 0, step: '0.01' },
            input: {
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            },
          }}
          helperText="Enter the amount of cash received from the customer to calculate the change due."
          fullWidth
          autoFocus
        />
      </Stack>
    </SmallModal>
  );
}

export default CashTenderModal;
